import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { fetchCompanyHomeTestingUserDetail } from './companyHomeTestingApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  ProgressList,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_COLORS,
  toneChip
} from '../../../../component/common/designSystem';

/**
 * Home Testing → User profile.
 *
 * One person's kits: how many they ordered, how many came back, which tests
 * they chose, and where each order currently sits.
 *
 * The order history deliberately carries three dates and a stage and nothing
 * else. What a test found — the biomarker values, the report PDF, the clinical
 * comment — is never read, and neither is the partner's copy of the person's
 * date of birth, phone or postal address. A company admin can see that someone
 * ordered a kit and whether it came back; what it said is between them and
 * their clinician.
 *
 * `fetcher`/`backPath`/`backLabel` are props so a super-admin Home Testing hub
 * can reuse the page against the unscoped endpoint.
 */
const HomeTestingUserDetail = ({
  fetcher = fetchCompanyHomeTestingUserDetail,
  backPath,
  backLabel
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const topKits = detail?.topKits || [];
  const statusBreakdown = detail?.statusBreakdown || [];
  const orderHistory = detail?.orderHistory || [];

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard icon={STAT_ICONS.catalog} title="Kits Ordered" value={user ? user.orders : '-'} />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Reached the Lab"
          value={user ? user.received : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Results In"
          value={user ? user.withResults : '-'}
        />
        <StatCard
          icon={STAT_ICONS.more}
          title="Not Returned"
          value={user ? user.awaitingReturn : '-'}
        />
        {/* Blank rather than 0% when they have never ordered — "hasn't used the
            benefit" and "never sends kits back" are very different things. */}
        <StatCard
          icon={STAT_ICONS.score}
          title="Return Rate"
          value={user?.orders > 0 ? `${user.returnRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Last Ordered"
          value={user?.lastOrderDate || '-'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <Panel title="Tests They Choose" subtitle="Most often ordered">
          {topKits.length > 0 ? (
            <ProgressList items={topKits} unit="orders" color={CHART_COLORS.brand} />
          ) : (
            <PanelEmpty>No kits ordered yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Partner Status" subtitle="What Vitall reports for their kits">
          {statusBreakdown.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium text-right">Kits</th>
                </>
              }
            >
              {statusBreakdown.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 text-right">{row.count}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No kits ordered yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Testing History" subtitle="First and most recent order">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First kit ordered</td>
              <td className="py-2 text-right">{user?.firstOrderDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last kit ordered</td>
              <td className="py-2 text-right">{user?.lastOrderDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Kits ordered</td>
              <td className="py-2 text-right">{user ? user.orders : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Results returned</td>
              <td className="py-2 text-right">{user ? user.withResults : '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>

      {/* Dates and a stage. Never a result. */}
      <div className="mt-6">
        <Panel title="Their Kits" subtitle="Most recent orders first">
          {orderHistory.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Test</th>
                  <th className="py-2 px-4 font-medium text-center">Ordered</th>
                  <th className="py-2 px-4 font-medium text-center">At the Lab</th>
                  <th className="py-2 px-4 font-medium text-center">Results</th>
                  <th className="py-2 font-medium text-right">Stage</th>
                </>
              }
            >
              {orderHistory.map((row, index) => (
                <tr key={`${row.orderedOn}-${row.kits}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.kits}</td>
                  <td className="py-2 px-4 text-center">{row.orderedOn || '-'}</td>
                  <td className="py-2 px-4 text-center">{row.receivedOn || '-'}</td>
                  <td className="py-2 px-4 text-center">{row.resultsOn || '-'}</td>
                  <td className="py-2 text-right">{row.stage}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No kits ordered yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

HomeTestingUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

HomeTestingUserDetail.defaultProps = {
  backLabel: 'Back to Employees'
};

export default HomeTestingUserDetail;
